/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import get from 'lodash.get';
import type { UIComponent, FeedEntry } from '~/services/layout';
import {
  getBreakpointMediaQuery,
  getCellPluginConfigurationIds,
  getCellStylesFieldsByBreakpoint,
  responsiveBreakpointNames,
  type ResponsiveBreakpointNames,
  type ResponsiveCellType,
} from '~/services/layout/layout';
import logger from '~/services/logger';

const buttonsContainerButtonBorderColorMap: Map<string, string> = new Map([
  ['button1_border_color', '--button1_border_color'],
  ['button2_border_color', '--button2_border_color'],
  ['button3_border_color', '--button3_border_color'],
  ['button1_hover_border_color', '--button1_hover_border_color'],
  ['button2_hover_border_color', '--button2_hover_border_color'],
  ['button3_hover_border_color', '--button3_hover_border_color'],
]);

const buttonsContainerButtonColorMap: Map<string, string> = new Map([
  ['button1_background_color', '--button1_background_color'],
  ['button2_background_color', '--button2_background_color'],
  ['button3_background_color', '--button3_background_color'],
  ['button1_hover_background_color', '--button1_hover_background_color'],
  ['button2_hover_background_color', '--button2_hover_background_color'],
  ['button3_hover_background_color', '--button3_hover_background_color'],

  ['button1_font_color', '--button1_font_color'],
  ['button2_font_color', '--button2_font_color'],
  ['button3_font_color', '--button3_font_color'],
  ['button1_hover_font_color', '--button1_hover_font_color'],
  ['button2_hover_font_color', '--button2_hover_font_color'],
  ['button3_hover_font_color', '--button3_hover_font_color'],
]);

const cellImageBorderColorMap: Map<string, string> = new Map([
  ['image_border_color', '--image-border-color'],
  ['image_hover_border_color', '--image-hover-border-color'],
]);

const cellBorderColorMap: Map<string, string> = new Map([
  ['cell_border_color', '--cell-border-color'],
  ['cell_hover_border_color', '--cell-hover-border-color'],
]);

const cellImagePlaceholderColorMap: Map<string, string> = new Map([
  ['image_placeholder_color', '--bg-image-placeholder'],
]);

const cellImageShadowColorMap: Map<string, string> = new Map([
  ['image_shadow_color', '--image_shadow_color'],
  ['image_hover_shadow_color', '--image_hover_shadow_color'],
]);

const cellColorMap: Map<string, string> = new Map([
  ['cell_background_color', '--cell_background_color'],
  ['cell_background_color_hover', '--cell_background_color_hover'],
  ['cell_background_color_selected', '--cell_background_color_selected'],
  [
    'cell_background_color_selected_and_hover',
    '--cell_background_color_selected_and_hover',
  ],
]);

const infoCellTextLabelColorMap: Map<string, string> = new Map([
  ...['title_text_font_color', 'button_text_font_color'].map(
    (key) => [key, '--text-label-color'] as const
  ),
  ['button_text_font_color_hover', '--text-label-color-hover'],
  ['title_text_background_color', '--bg-label'] as const,
  ['title_text_background_color_hover', '--bg-label-hover'] as const,
]);

const infoCellButtonElementBorderColorMap: Map<string, string> = new Map([
  ['button_element_border_color', '--button_element_border_color'],
]);

const infoCellButtonElementBackgroundColorMap: Map<string, string> = new Map([
  ['button_element_background_color', '--button_element_background_color'],
]);

const infoCellBorderColorMap: Map<string, string> = new Map([
  ['cell_border_top_color', '--cell-info-border-top-color'],
  ['cell_border_bottom_color', '--cell-info-border-bottom-color'],
]);

const textLabelColorMap: Map<string, string> = new Map([
  // #region font colors
  ...[
    'text_label_1_font_color',
    'text_label_2_font_color',
    'text_label_3_font_color',
    'text_label_4_font_color',
    'top_text_label_1_font_color',
    'top_text_label_2_font_color',
    'top_text_label_3_font_color',
    'center_text_label_1_font_color',
    'center_text_label_2_font_color',
    'center_text_label_3_font_color',
    'bottom_text_label_1_font_color',
    'bottom_text_label_2_font_color',
    'bottom_text_label_3_font_color',
  ].map((key) => [key, '--text-label-color'] as const),
  ...[
    'text_label_1_font_color_hover',
    'text_label_2_font_color_hover',
    'text_label_3_font_color_hover',
    'text_label_4_font_color_hover',
    'top_text_label_1_font_color_hover',
    'top_text_label_2_font_color_hover',
    'top_text_label_3_font_color_hover',
    'center_text_label_1_font_color_hover',
    'center_text_label_2_font_color_hover',
    'center_text_label_3_font_color_hover',
    'bottom_text_label_1_font_color_hover',
    'bottom_text_label_2_font_color_hover',
    'bottom_text_label_3_font_color_hover',
  ].map((key) => [key, '--text-label-color-hover'] as const),
  ...[
    'text_label_1_font_color_selected',
    'text_label_2_font_color_selected',
    'text_label_3_font_color_selected',
    'text_label_4_font_color_selected',
    'top_text_label_1_font_color_selected',
    'top_text_label_2_font_color_selected',
    'top_text_label_3_font_color_selected',
    'center_text_label_1_font_color_selected',
    'center_text_label_2_font_color_selected',
    'center_text_label_3_font_color_selected',
    'bottom_text_label_1_font_color_selected',
    'bottom_text_label_2_font_color_selected',
    'bottom_text_label_3_font_color_selected',
  ].map((key) => [key, '--text-label-color-selected'] as const),
  ...[
    'text_label_1_font_color_selected_and_hover',
    'text_label_2_font_color_selected_and_hover',
    'text_label_3_font_color_selected_and_hover',
    'text_label_4_font_color_selected_and_hover',
    'top_text_label_1_font_color_selected_and_hover',
    'top_text_label_2_font_color_selected_and_hover',
    'top_text_label_3_font_color_selected_and_hover',
    'center_text_label_1_font_color_selected_and_hover',
    'center_text_label_2_font_color_selected_and_hover',
    'center_text_label_3_font_color_selected_and_hover',
    'bottom_text_label_1_font_color_selected_and_hover',
    'bottom_text_label_2_font_color_selected_and_hover',
    'bottom_text_label_3_font_color_selected_and_hover',
  ].map((key) => [key, '--text-label-color-selected-and-hover'] as const),
  // #endregion font colors

  // #region background colors
  ...[
    'text_label_1_background_color',
    'text_label_2_background_color',
    'text_label_3_background_color',
    'text_label_4_background_color',
    'top_text_label_1_background_color',
    'top_text_label_2_background_color',
    'top_text_label_3_background_color',
    'center_text_label_1_background_color',
    'center_text_label_2_background_color',
    'center_text_label_3_background_color',
    'bottom_text_label_1_background_color',
    'bottom_text_label_2_background_color',
    'bottom_text_label_3_background_color',
  ].map((key) => [key, '--bg-label'] as const),
  ...[
    'text_label_1_hover_background_color',
    'text_label_2_hover_background_color',
    'text_label_3_hover_background_color',
    'text_label_4_hover_background_color',
    'top_text_label_1_hover_background_color',
    'top_text_label_2_hover_background_color',
    'top_text_label_3_hover_background_color',
    'center_text_label_1_hover_background_color',
    'center_text_label_2_hover_background_color',
    'center_text_label_3_hover_background_color',
    'bottom_text_label_1_hover_background_color',
    'bottom_text_label_2_hover_background_color',
    'bottom_text_label_3_hover_background_color',
  ].map((key) => [key, '--bg-label-hover'] as const),
  // #endregion background colors
]);

const runtimeBackgroundColorMap: Map<string, string> = new Map([
  ['runtime_bg_color', '--runtime-bg-color'],
]);

const runtimeFontColorMap: Map<string, string> = new Map([
  ['runtime_font_color', '--runtime-font-color'],
]);

const progressBarColorMap: Map<string, string> = new Map([
  ['progress_bar_progress_bar_color', '--progress_bar_progress_bar_color'],
]);

const progressBarTotalColorMap: Map<string, string> = new Map([
  ['progress_bar_total_bar_color', '--progress_bar_total_bar_color'],
]);

export function getDynamicColors({
  styleKeyCssVarMap,
  entry,
  stylesByBreakpoint,
  cellIdsByBreakpoint,
  isSameElement = false,
  cellClassNameSuffix,
}: {
  styleKeyCssVarMap: Map<string, string>;
  entry: FeedEntry;
  stylesByBreakpoint: Record<ResponsiveBreakpointNames, Record<string, any>>;
  cellIdsByBreakpoint: Record<ResponsiveBreakpointNames, string>;
  isSameElement?: boolean;
  cellClassNameSuffix?: string;
}) {
  try {
    const dynamicColors: string[] = [];

    responsiveBreakpointNames.forEach((bp) => {
      const breakpointStyles = stylesByBreakpoint[bp];
      if (!breakpointStyles) return;

      [...styleKeyCssVarMap.keys()].forEach((key) => {
        const value = breakpointStyles[key];
        if (typeof value !== 'string' || !value.includes('extensions.')) return;

        const cssVar = styleKeyCssVarMap.get(key);
        if (!cssVar) return;

        let color = get(entry, value);
        if (!color || typeof color !== 'string') return;

        color = color.replaceAll(' ', '');

        const breakpointMediaQuery = getBreakpointMediaQuery(
          bp as ResponsiveCellType
        );

        let cssAttrPrefix = `${breakpointMediaQuery} { ${
          isSameElement ? '&' : ''
        }.cell-v2-${bp}-${cellIdsByBreakpoint[bp]}`;

        if (cellClassNameSuffix) {
          const regExpMatchArray =
            key.match(new RegExp(`${cellClassNameSuffix}_(\\d)`)) ||
            key.match(new RegExp(`(${cellClassNameSuffix})`));

          if (!regExpMatchArray) return;

          regExpMatchArray &&
            (cssAttrPrefix = cssAttrPrefix.concat(`-${regExpMatchArray[0]}`));
        }

        dynamicColors.push(
          `${cssAttrPrefix} ${
            isSameElement ? '' : '&'
          } { ${cssVar}: ${color} !important; }}`
        );
      });
    });

    return css(dynamicColors.join(''));
  } catch (error: any) {
    logger.error(`Get Dynamic Colors: ${error.message}`);
    return css``;
  }
}

export type AllDynamicColorsReturnType = {
  cell: ReturnType<typeof css>;
  cellBorder: ReturnType<typeof css>;
  cellImage: {
    border: ReturnType<typeof css>;
    placeholder: ReturnType<typeof css>;
    shadow: ReturnType<typeof css>;
  };
  buttonsContainer: {
    buttonBorder: ReturnType<typeof css>;
    button: ReturnType<typeof css>;
  };
  textLabel: ReturnType<typeof css>;
  infoCell: {
    title: ReturnType<typeof css>;
    border: ReturnType<typeof css>;
    button: ReturnType<typeof css>;
    buttonElementBorder: ReturnType<typeof css>;
    buttonElementBackground: ReturnType<typeof css>;
  };
  runtime: {
    background: ReturnType<typeof css>;
    font: ReturnType<typeof css>;
  };
  progressBar: {
    bar: ReturnType<typeof css>;
    total: ReturnType<typeof css>;
  };
};

export function getAllDynamicColors({
  entry,
  uiComponent,
  cellStyles,
}: {
  entry: FeedEntry;
  uiComponent: UIComponent;
  cellStyles: Record<string, any>;
}): AllDynamicColorsReturnType | {} {
  try {
    const { mobileCellId, tabletCellId, desktopCellId, largeDesktopCellId } =
      getCellPluginConfigurationIds(uiComponent);

    const cellIdsByBreakpoint: Record<ResponsiveBreakpointNames, string> = {
      mobile: mobileCellId,
      tablet: tabletCellId,
      desktop: desktopCellId,
      'large-desktop': largeDesktopCellId,
    };

    const stylesByBreakpoint = getCellStylesFieldsByBreakpoint({
      uiComponent,
      cellStyles,
      filterByFieldIncludes: 'color',
    });

    const assetsByBreakpoint = getCellStylesFieldsByBreakpoint({
      uiComponent,
      cellStyles,
      configurationCategory: 'assets',
      filterByFieldIncludes: 'color',
    });

    const commonArgs = {
      entry,
      stylesByBreakpoint,
      cellIdsByBreakpoint,
    };

    return {
      cell: getDynamicColors({
        styleKeyCssVarMap: cellColorMap,
        ...commonArgs,
        isSameElement: true,
      }),
      cellBorder: getDynamicColors({
        styleKeyCssVarMap: cellBorderColorMap,
        ...commonArgs,
      }),
      cellImage: {
        border: getDynamicColors({
          styleKeyCssVarMap: cellImageBorderColorMap,
          entry,
          stylesByBreakpoint: assetsByBreakpoint,
          cellIdsByBreakpoint,
        }),
        placeholder: getDynamicColors({
          styleKeyCssVarMap: cellImagePlaceholderColorMap,
          entry,
          stylesByBreakpoint: assetsByBreakpoint,
          cellIdsByBreakpoint,
        }),
        shadow: getDynamicColors({
          styleKeyCssVarMap: cellImageShadowColorMap,
          entry,
          stylesByBreakpoint: assetsByBreakpoint,
          cellIdsByBreakpoint,
        }),
      },
      buttonsContainer: {
        buttonBorder: getDynamicColors({
          styleKeyCssVarMap: buttonsContainerButtonBorderColorMap,
          entry,
          stylesByBreakpoint: assetsByBreakpoint,
          cellIdsByBreakpoint,
        }),
        button: getDynamicColors({
          styleKeyCssVarMap: buttonsContainerButtonColorMap,
          entry,
          stylesByBreakpoint: assetsByBreakpoint,
          cellIdsByBreakpoint,
        }),
      },
      textLabel: getDynamicColors({
        styleKeyCssVarMap: textLabelColorMap,
        cellClassNameSuffix: 'text_label',
        ...commonArgs,
      }),
      infoCell: {
        title: getDynamicColors({
          styleKeyCssVarMap: infoCellTextLabelColorMap,
          cellClassNameSuffix: 'title_text',
          ...commonArgs,
        }),
        border: getDynamicColors({
          styleKeyCssVarMap: infoCellBorderColorMap,
          ...commonArgs,
        }),
        button: getDynamicColors({
          styleKeyCssVarMap: infoCellTextLabelColorMap,
          ...commonArgs,
        }),
        buttonElementBorder: getDynamicColors({
          styleKeyCssVarMap: infoCellButtonElementBorderColorMap,
          ...commonArgs,
        }),
        buttonElementBackground: getDynamicColors({
          styleKeyCssVarMap: infoCellButtonElementBackgroundColorMap,
          ...commonArgs,
        }),
      },
      runtime: {
        background: getDynamicColors({
          styleKeyCssVarMap: runtimeBackgroundColorMap,
          ...commonArgs,
        }),
        font: getDynamicColors({
          styleKeyCssVarMap: runtimeFontColorMap,
          ...commonArgs,
        }),
      },
      progressBar: {
        bar: getDynamicColors({
          styleKeyCssVarMap: progressBarColorMap,
          entry,
          stylesByBreakpoint: assetsByBreakpoint,
          cellIdsByBreakpoint,
        }),
        total: getDynamicColors({
          styleKeyCssVarMap: progressBarTotalColorMap,
          entry,
          stylesByBreakpoint: assetsByBreakpoint,
          cellIdsByBreakpoint,
        }),
      },
    };
  } catch (error: any) {
    logger.error(`Get All Dynamic Colors: ${error.message}`);
    return {};
  }
}
